import DropdownHover from '@components/Dropdown/DropdownHover'
import { Button } from 'primereact/button'
import { useTranslation } from 'react-i18next'

/**
 * Renders the actions bar above a list/table: arbitrary content passed as children, plus an optional
 * "show/hide filters" toggle button used for backend filtering rendered as inline table inputs.
 *
 * @component
 * @param {Object} props
 * @param {Array} [props.entries] - Additional action menu entries (objects with label/icon/command, or
 *   items for a dropdown) prepended to the generated action buttons.
 * @param {Boolean} [props.loading] - Whether filter values are loading; disables the filters button and
 *   shows a spinner icon.
 * @param {React.ReactNode} [props.children] - Custom content rendered before the generated action buttons.
 * @param {Boolean} [props.showFilters] - Whether filters are currently shown as inline inputs in the table.
 * @param {Function} [props.setShowFilters] - Callback to toggle `showFilters`; when provided, a
 *   "show/hide filters" button is rendered.
 * @returns {JSX.Element}
 */
const ListActions = ({ entries, loading, children, showFilters, setShowFilters }) => {
  const { t } = useTranslation()
  const items = []

  entries && items.push(...entries)

  /**
   * Builds a filters menu entry (label, icon, and click command) for the generated action buttons.
   *
   * @param {Function} command - Callback invoked when the entry is clicked.
   * @returns {{
   *   label: string, // Translated "Filters" label.
   *   icon: string, // Icon class: spinner while loading, otherwise eye/filter depending on showFilters.
   *   className: string, // Fixed "button-filters" class name.
   *   disabled: Boolean, // Whether the entry is disabled while loading.
   *   command: Function // The provided command callback.
   * }}
   */
  const buildFilterEntry = command => {
    return {
      label: t('common.filters'),
      icon: loading
        ? 'fa-solid fa-spinner fa-spin'
        : showFilters
          ? 'fa-solid fa-eye-slash'
          : 'fa-solid fa-filter',
      className: 'button-filters',
      disabled: loading,
      command
    }
  }

  // if filters in ListActions, we add the command to show them
  // if we have the props setShowFilters, to show filters directly in Datatable, we use it instead
  setShowFilters && items.push(buildFilterEntry(() => setShowFilters(!showFilters)))

  return (
    <div className="list-actions">
      <div className="list-actions-top">
        {children}
        {!!items.length &&
          items.map((item, index) =>
            item.items ? (
              <DropdownHover
                key={index}
                icon={item.icon}
                label={item.label}
                items={item.items}
                disabled={item.disabled}
                className={item.className}
              />
            ) : (
              <Button
                rounded
                key={index}
                label={item.label}
                icon={item.icon}
                onClick={item.command}
                disabled={item.disabled}
                className={item.className}
                type="button"
                text
              />
            )
          )}
      </div>
    </div>
  )
}

export default ListActions
